import { FunctionComponent } from 'react'

import styled, { css } from 'styled-components'

import { mobileCSS } from '@measures/responsive'
import FreeRatioImage from '@components/FreeRatioImage'
import SvgIcon from '@components/SvgIcon'

import {
  constraints as replayLogoConstraints,
  replayLogoImage,
} from './images/replayLogo'
import { CommonLottoData, EuromillionsData, SwisslottoData } from './types'

interface NumbersProps {
  numbers: CommonLottoData['numbers']
  adjunctNumbers?: EuromillionsData['adjunct-numbers']
  luckyNumber?: SwisslottoData['luckynumber']
  replayNumber?: SwisslottoData['replay-number']
}

const StyledNumbers = styled.div`
  display: flex;
  margin: 17px 0 13.5px -5px;
  flex-direction: row;
  ${mobileCSS(css`
    flex-direction: column;
    margin-bottom: 21px;
  `)}
`

const FirstRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  ${mobileCSS(css`
    margin-bottom: 21px;
  `)}
`

interface NumberProps {
  isLucky?: boolean
  isReplay?: boolean
}

const Number = styled.div<NumberProps>`
  ${({
    theme: {
      colors: {
        staticYellow500,
        fillBrand,
        fillDefault,
        staticWhite,
        staticBlack,
      },
      typography: {
        headings: {
          xsmall: { bundledCSS: headingXSmallCSS },
        },
      },
    },
    isLucky,
    isReplay,
  }) => css`
    ${headingXSmallCSS};
    display: flex;
    width: 30px;
    height: 30px;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    background-color: ${isReplay
      ? fillBrand
      : isLucky
        ? staticYellow500
        : fillDefault};
    margin-left: 5px;
    ${isReplay &&
    css`
      color: ${staticWhite};
      margin-left: 30px;
      ${mobileCSS(css`
        margin-left: 5px;
      `)}
    `}

    ${isLucky &&
    css`
      color: ${staticBlack};
    `}
  `}
`

const AdjunctNumber = styled.div`
  ${({
    theme: {
      colors: { staticBlack },
      typography: {
        headings: {
          xsmall: { bundledCSS: headingXSmallCSS },
        },
      },
    },
  }) => css`
    ${headingXSmallCSS};
    display: flex;
    width: 34px;
    height: 34px;
    justify-content: center;
    align-items: center;
    color: ${staticBlack};
    position: relative;
    z-index: 2;
  `}
`

const StyledStarIcon = styled(SvgIcon)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`

const AdjunctNumberContainer = styled.div`
  position: relative;
  width: 33px;
  height: 33px;
  margin-left: 7px;
`

const StyledReplayLogo = styled(FreeRatioImage)`
  margin-left: 7px;
`

const Numbers: FunctionComponent<NumbersProps> = ({
  numbers,
  adjunctNumbers,
  luckyNumber,
  replayNumber,
}) => {
  return (
    <StyledNumbers>
      <FirstRowContainer>
        {numbers.map((number, index) => (
          <Number key={`number-${index}`}>{number}</Number>
        ))}
        {adjunctNumbers?.map((adjunctNumber, index) => (
          <AdjunctNumberContainer key={`adjunctNumber-${index}`}>
            <StyledStarIcon iconName="lotto-star" size={33} />
            <AdjunctNumber>{adjunctNumber}</AdjunctNumber>
          </AdjunctNumberContainer>
        ))}
        {luckyNumber && <Number isLucky>{luckyNumber}</Number>}
      </FirstRowContainer>
      {replayNumber && (
        <FirstRowContainer>
          <Number isReplay>{replayNumber}</Number>
          <StyledReplayLogo
            src={replayLogoImage.src}
            alt={replayLogoImage.alt}
            originalDimensions={{
              width: replayLogoImage.width,
              height: replayLogoImage.height,
            }}
            crops={replayLogoImage.crops}
            constraints={replayLogoConstraints}
          />
        </FirstRowContainer>
      )}
    </StyledNumbers>
  )
}

export default Numbers
